import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { WithLayout } from "../components/Layout";
import { SEO } from "../components/SEO";
import { BookingMapWidget2 } from "../components/BookingMapWidget2";
import { BookingMapWidgetLinkGoToSection } from "../components/BookingMapWidgetLinkGoToSection";
import { AmazonWidget } from "../components/AmazonWidget";
import * as helpers from "../helpers";

const SingleTitleAndStory = (title, story) => {
  return (
    <React.Fragment key={`__SingleTitleAndStory__${title}`}>
      <span
        dangerouslySetInnerHTML={{
          __html: title,
        }}
      />
      <span
        dangerouslySetInnerHTML={{
          __html: story,
        }}
      />
    </React.Fragment>
  );
};

const ListItemsHTMLSnippets = ({
  id,
  titles,
  stories,
  imagesArr,
  frontmatter,
  i18n,
}) => {
  let aoc = [];
  if (titles?.length > 0) {
    titles.forEach((title, index) => {
      if (stories[index]) {
        aoc.push(SingleTitleAndStory(title, stories[index]));
        aoc.push(
          <GatsbyImage
            image={imagesArr[index + 1] || imagesArr[0]}
            key={`image__${index}`}
            alt={title.replace("<h3>", "").replace("</h3>", "")}
          />
        );
        if (
          helpers.isTravelDomain(process.env.GATSBY_NODE_BUILD_CODE_BY_DOMAIN)
        ) {
          aoc.push(
            <BookingMapWidgetLinkGoToSection
              frontmatter={frontmatter}
              i18n_prefix={i18n.i18n_current}
              i18n_main={i18n.i18n_main}
              key={`section_link__${index}`}
            />
          );
        }
      }
    });
  }

  return <span id={id}>{aoc}</span>;
};

const TemplateArticle = React.memo(
  ({ pageContext: { node, i18n, order, pagePath, url_slug, config } }) => {
    /** NOTE! pageContext (gatsby-node context) is not the same as LayoutContext (react context) */

    const frontmatter = node.frontmatter;

    const i18nSetup = {};
    const i18nSetupEmpty = [
      "html_article_title",
      "html_article_subtitle",
      "html_article_brief",
      "html_optimal_route",
      "image_caption",
      "image_alt_text",
      "seo_meta_title",
      "seo_meta_keywords",
      "seo_meta_description",
      "list_item_html_titles",
      "list_item_html_stories",
    ];

    const i18nSetupWithLocale = i18nSetupEmpty.map(ll => {
      const resp = `${ll}_${i18n?.i18n_current}`;

      return resp;
    });

    i18nSetupWithLocale.forEach((clc, index) => {
      i18nSetup[i18nSetupEmpty[index]] = frontmatter[clc];
    });

    /**
     * pagination
     */
    // console.log(
    //   "🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️" +
    //     // JSON.stringify({ config }) +
    //     frontmatter[
    //       `main_item_translated_${i18n?.i18n_prefix || i18n?.i18n_main}`
    //     ] +
    //     config[`pagination_category_${i18n?.i18n_prefix || i18n?.i18n_main}`] +
    //     config[`pagination_main_${i18n?.i18n_prefix || i18n?.i18n_main}`]
    // );
    const pagination = {
      main_item_translated_:
        frontmatter[
          `main_item_translated_${i18n?.i18n_prefix || i18n?.i18n_main}`
        ],
      pagination_category:
        config[`pagination_category_${i18n?.i18n_prefix || i18n?.i18n_main}`],
      pagination_main:
        config[`pagination_main_${i18n?.i18n_prefix || i18n?.i18n_main}`],
    };

    /**
     * from static query
     */

    const sortedImagesFromGatsby = helpers.sortImagesFromGatsby(
      frontmatter.images_array
    );
    const imagesArr = sortedImagesFromGatsby.map(img_path => {
      return getImage(img_path);
    });

    const bookingMapWidgetFromQuery = frontmatter.booking_map_widget || "n/a";
    const otherImagesEstimatedStart = i18nSetup.list_item_html_titles.length;
    const otherImagesEstimatedEnd = imagesArr.length - 1;
    let otherImages = [];

    const showAlt = helpers.showAlt(
      process.env.GATSBY_NODE_BUILD_CODE_BY_DOMAIN
    );

    if (
      otherImagesEstimatedStart > 0 &&
      otherImagesEstimatedStart < otherImagesEstimatedEnd
    ) {
      for (
        let i = otherImagesEstimatedStart;
        i < otherImagesEstimatedEnd;
        i++
      ) {
        otherImages.push(
          <GatsbyImage
            image={imagesArr[i]}
            alt={
              !!showAlt ? i18nSetup.image_alt_text : i18nSetup.seo_meta_title
            }
          />
        );
      }
    }

    /**
     *  schema.org
     */

    // console.log("🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️🖼️");
    const schemaImages = imagesArr.map(ib =>
      ib && ib?.images
        ? ib.images.sources[0].srcSet.split(",")[0].split(" ")[0]
        : []
    );

    const dpublished = !!frontmatter.timestamp_creation_utc
      ? new Date(frontmatter.timestamp_creation_utc).toISOString()
      : new Date().toISOString();

    // const imagesArrSchema = imagesArr.map(gatsby_image_object => {
    //   console.dir({
    //     FROM: "schema for gatsby_image_object✨✨✨✨✨✨",
    //     gatsby_image_object: gatsby_image_object,
    //   });
    //   return {
    //     "@context": "https://schema.org",
    //     "@type": "ImageObject",
    //     url:
    //       process.env.GATSBY_SITE_URL + gatsby_image_object.images.fallback.src,
    //     width: gatsby_image_object.width,
    //     height: gatsby_image_object.height,
    //   };
    // });

    false &&
      console.dir({
        FROM: "schema✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨✨",
        title: i18nSetup.html_article_title,
        name: i18nSetup.seo_meta_title,
        description: i18nSetup.seo_meta_description,
        images: imagesArr.map(gatsby_image_object => ({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          url:
            process.env.GATSBY_SITE_URL +
            gatsby_image_object.images.fallback.src,
          width: gatsby_image_object.width,
          height: gatsby_image_object.height,
        })),
        url: process.env.GATSBY_SITE_URL + pagePath,
        datePublished: dpublished,
        // articleBody:
        //   i18nSetup.html_article_title +
        //   i18nSetup.image_caption +
        //   i18nSetup.html_article_subtitle +
        //   i18nSetup.html_article_brief +
        //   i18nSetup.html_optimal_route,
        keywords: i18nSetup.seo_meta_keywords,
        lang: i18n.i18n_current ?? i18n.i18n_main, // ToDo: main
        // imagesArrSchema: imagesArrSchema,
      });
    const schema = helpers.generateBlogPostSchema({
      title: i18nSetup.html_article_title,
      name: i18nSetup.seo_meta_title,
      description: i18nSetup.seo_meta_description,
      images: schemaImages,
      url: process.env.GATSBY_SITE_URL + pagePath,
      datePublished: dpublished,
      articleBody:
        i18nSetup.html_article_title +
        i18nSetup.image_caption +
        i18nSetup.html_article_subtitle +
        i18nSetup.html_article_brief +
        i18nSetup.html_optimal_route,
      keywords: i18nSetup.seo_meta_keywords,
      lang: i18n.i18n_current ?? i18n.i18n_main, // ToDo: main
    });

    /**
     * affilate links
     */
    const bookingWidgetText = helpers.generateBookingWidgetText(
      config,
      frontmatter,
      i18n.i18n_current,
      i18n.i18n_main
    );

    const amazonLink = config.amazon_custom_link
      ? config.amazon_custom_link
      : config.amazon_default_link;
    const amazonWidgetTextHTML = helpers.generateAmazonWidgetTextHTML(
      config,
      frontmatter,
      amazonLink,
      i18n.i18n_current,
      i18n.i18n_main
    );
    const optimalRouteTitle = helpers.generateOptimalRouteTitle(
      config,
      frontmatter,
      i18n.i18n_current,
      i18n.i18n_main
    );
    const hawsTop = helpers.affiliateWidgetSelectTop(
      process.env.GATSBY_NODE_BUILD_CODE_BY_DOMAIN,
      frontmatter.structure
    );
    const hawsBottom = helpers.affiliateWidgetSelectBottom(
      process.env.GATSBY_NODE_BUILD_CODE_BY_DOMAIN,
      frontmatter.structure
    );

    return (
      <WithLayout pagePath={pagePath} i18n={i18n} pagination={pagination}>
        <SEO
          title={i18nSetup.seo_meta_title}
          keywords={i18nSetup.seo_meta_keywords}
          description={i18nSetup.seo_meta_description}
          pagePath={process.env.GATSBY_SITE_URL + pagePath}
          image={imagesArr[0]}
          schema={schema}
        />

        <div
          id="post-template-wrapper"
          className={`template-wrapper post-template-wrapper`}
          style={{ padding: "0 7.5px 0 7.5px" }}
        >
          <section aria-labelledby="article-title">
            <span
              dangerouslySetInnerHTML={{
                __html: i18nSetup.html_article_title,
              }}
            />

            <GatsbyImage
              image={imagesArr[0]}
              alt={
                i18nSetup.html_article_title + " " + !!showAlt
                  ? i18nSetup.image_alt_text
                  : i18nSetup.seo_meta_title
              }
            />

            <span
              dangerouslySetInnerHTML={{
                __html: i18nSetup.image_caption,
              }}
            />
          </section>

          <section aria-labelledby="article-subtitle">
            <span
              dangerouslySetInnerHTML={{
                __html: i18nSetup.html_article_subtitle,
              }}
            />

            <span
              dangerouslySetInnerHTML={{
                __html: i18nSetup.html_article_brief,
              }}
            />
          </section>

          <section aria-labelledby="optimal-route">
            <h3
              dangerouslySetInnerHTML={{
                __html: optimalRouteTitle,
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: i18nSetup.html_optimal_route,
              }}
            />

            <GatsbyImage
              image={imagesArr[imagesArr.length - 1]}
              alt={
                !!showAlt ? i18nSetup.image_alt_text : i18nSetup.seo_meta_title
              }
            />
          </section>

          {hawsTop !== helpers.WIDGET_TYPE_SELECTION.NONE &&
            (hawsTop === helpers.WIDGET_TYPE_SELECTION.TRAVEL ? (
              <section
                id="booking-map-widget"
                aria-labelledby="booking-map-widget"
              >
                <BookingMapWidget2
                  title={bookingWidgetText}
                  bookingMapWidgetFromQuery={
                    bookingMapWidgetFromQuery !== "ToDo"
                      ? bookingMapWidgetFromQuery
                      : config.booking_placeholder_map_widget
                  }
                />
              </section>
            ) : (
              <section aria-labelledby="amazon-shop-widget">
                <AmazonWidget amazonWidgetTextHTML={amazonWidgetTextHTML} />
              </section>
            ))}

          <section aria-labelledby="list-of-snippets">
            <ListItemsHTMLSnippets
              id="list-of-snippets"
              titles={i18nSetup.list_item_html_titles}
              stories={i18nSetup.list_item_html_stories}
              imagesArr={imagesArr}
              frontmatter={frontmatter}
              i18n={i18n}
            />
          </section>

          {hawsBottom !== helpers.WIDGET_TYPE_SELECTION.NONE &&
            (hawsBottom === helpers.WIDGET_TYPE_SELECTION.TRAVEL ? (
              <section aria-labelledby="booking-map-widget">
                <BookingMapWidget2
                  title={bookingWidgetText}
                  bookingMapWidgetFromQuery={
                    bookingMapWidgetFromQuery !== "ToDo"
                      ? bookingMapWidgetFromQuery
                      : config.booking_placeholder_map_widget
                  }
                />
              </section>
            ) : (
              <section aria-labelledby="amazon-shop-widget">
                <AmazonWidget amazonWidgetTextHTML={amazonWidgetTextHTML} />
              </section>
            ))}
        </div>
        {/* <LeftSide />
      <RightSide /> */}
      </WithLayout>
    );
  }
);

export default TemplateArticle;
